import { Injectable } from '@angular/core';
import {
  BasketAction,
  BASKET_CATEGORY,
} from '@epicuro-next/constants/platform/tracking/basket';
import { BasketReviewService } from '@epicuro-next/data/baskets';
import * as basketDto from '@epicuro-next/data/baskets-dto';
import { ExtendedDtoBasket } from '@epicuro-next/data/baskets-dto';
import { mapDtoStatus } from '@epicuro-next/data/baskets-grouping';
import { mapBasketUpdateRequest } from '@epicuro-next/data/baskets-update';
import { UsersApiService } from '@epicuro-next/data/users';
import {
  selectActiveAccount,
  selectActiveAccountId,
} from '@epicuro-next/state/accounts-state';
import {
  selectSiteId,
  selectSubSiteId,
} from '@epicuro-next/state/session-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import {
  gtmTrackActions,
  resetTrackingPayloadToState,
} from '@epicuro-next/state/tracking';
import { UBCMessageName } from '@epicuro-next/state/ubc-communication/core';
import {
  updateBasket,
  updateBasketError,
  updateBasketSuccess,
} from '@epicuro-next/state/ubc-communication/ubc-basket';
import {
  postUserPreferences,
  selectPreferredShippingOptionId,
} from '@epicuro-next/state/user-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, select, Store } from '@ngrx/store';
import {
  BasketAccountAddress,
  BasketAddressRowsGroupDetails,
  BasketHeader,
  BasketUpdateRequest,
} from 'graphql-api/baskets-api-interfaces';
import {
  combineLatest,
  merge,
  NEVER,
  Observable,
  of,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { CBasketService } from '../../services';
import {
  getUpdateBasketItemsRequest,
  isBasketStatusNewish,
} from '../../shared';
import * as basketActions from '../actions';
import {
  loadDtoBasketSuccess,
  pollBasket,
  setBasketOperableStatus,
} from '../actions';
import {
  accountsToBasketAccountsItems,
  selectActiveBasketId,
  selectAvailableShippingOptions,
  selectBasketAccounts,
  selectBasketDtoFromEntitiesById,
  selectBasketId,
  selectBasketPropertyBag,
  selectCustomOrderReferenceNumber,
  selectDeliveryShops,
  selectDtoBasketFromEntities,
  selectIsDropShipAllowed,
  selectIsDropShipSet,
  selectIsOperableStatus,
  selectOperableBasketId,
  selectOperableDtoBasketFromEntity,
  selectOperableExtendedBasketItems,
  selectPoNumberRequiredRule,
  selectPreselectPropertyBagRule,
  selectPropertyBagForAutoFill,
  selectSelectedShippingOptionId,
  selectShipInFullOption,
  selectShippingOptionUsePreferredRule,
  selectShops,
} from '../selectors';

/**
 * Basket state side effects
 */
@Injectable()
export class UpdateBasketEffects {
  /** Dependencies */
  constructor(
    private actions$: Actions,
    private basketService: CBasketService,
    private basketReviewService: BasketReviewService,
    private store: Store,
    private userService: UsersApiService,
  ) {}
  private updateBasketActions$ = merge(
    this.actions$.pipe(
      ofType(basketActions.updateBasketItems),
      map(({ type, ...itemsToUpdate }) => ({
        itemsToUpdate,
        source: undefined,
        strategy: undefined,
      })),
    ),
    this.actions$.pipe(
      ofType(updateBasket),
      map((action) => ({
        itemsToUpdate: action.payload.shops,
        source: action.payload.initiatorSource,
        strategy: action.payload.updateStrategy,
      })),
    ),
  );

  public updateBasketItems$ = createEffect(() =>
    this.updateBasketActions$.pipe(
      switchMap(({ itemsToUpdate, source, strategy }) =>
        this.getBasketHeader().pipe(
          concatLatestFrom(() => [
            this.store.pipe(select(selectOperableExtendedBasketItems)),
            this.store.pipe(select(selectDeliveryShops)),
          ]),
          map(([basketHeader, basketItems, deliveryShops]) => ({
            request: getUpdateBasketItemsRequest(
              itemsToUpdate,
              basketHeader,
              basketItems,
              deliveryShops,
              strategy,
            ),
            source,
          })),
          switchMap(({ request, source: initiatorSource }) => {
            return this.updateBasketRequest(request).pipe(
              map(() => {
                if (initiatorSource) {
                  return updateBasketSuccess({
                    messageName: UBCMessageName.UpdateBasketSuccess,
                    payload: {
                      source: initiatorSource,
                      basketId: request.basketHeader.basketId,
                    },
                  });
                }

                return basketActions.updateBasketItemsSuccess();
              }),
            );
          }),
          catchError((error) =>
            of(
              source
                ? updateBasketError({
                    messageName: UBCMessageName.UpdateBasketError,
                    payload: { source },
                  })
                : basketActions.updateBasketItemsFailure({
                    error: error.message,
                  }),
            ),
          ),
        ),
      ),
    ),
  );

  public resetTrackingPayloadToState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.updateBasketItemsSuccess),
      map(() => resetTrackingPayloadToState()),
    ),
  );

  public resetDropShipOnBasketUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        basketActions.updateBasketShopsSuccess,
        basketActions.updateDtoBasketSuccess,
      ),
      switchMap(() => {
        return this.store.pipe(
          select(selectIsDropShipSet),
          take(1),
          filter(Boolean),
        );
      }),
      switchMap(() =>
        this.store.pipe(
          select(selectIsDropShipAllowed),
          take(1),
          filter((isAllowed) => isAllowed === false),
          map(() => basketActions.setDropShip({ payload: null })),
        ),
      ),
    ),
  );

  public shopsUpdateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        basketActions.moveBasketSuccess,
        basketActions.updateBasketShopsSuccess,
      ),
      concatLatestFrom(() => this.store.pipe(select(selectActiveBasketId))),
      switchMap(([{ basketId }, activeBasketId]) => {
        const actions: Action[] = [basketActions.clearBasketValidations()];

        if (basketId === activeBasketId) {
          actions.push(basketActions.updateActiveBasketAccounts());
        }

        return actions;
      }),
    ),
  );

  public updateBasketShops$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basketActions.updateBasketShops),
      concatLatestFrom(() => this.store.pipe(select(selectBasketAccounts))),
      map(([{ accounts }, previousAccounts]) => ({
        currentBasketAccounts: accountsToBasketAccountsItems(accounts),
        previousBasketAccounts: accountsToBasketAccountsItems(previousAccounts),
      })),
      concatLatestFrom(() => [
        this.store.pipe(select(selectDtoBasketFromEntities)),
      ]),
      switchMap(
        ([{ currentBasketAccounts, previousBasketAccounts }, basket]) => {
          if (!basket) return NEVER;

          const accountsToRemove = getBasketAccountsDiff(
            previousBasketAccounts,
            currentBasketAccounts,
          );

          const accountsToAdd = getBasketAccountsDiff(
            currentBasketAccounts,
            previousBasketAccounts,
          );

          return this.updateBasketAccountsRequest(
            basket,
            accountsToAdd,
            accountsToRemove,
          ).pipe(
            map((updatedBasket) =>
              basketActions.updateBasketShopsSuccess({
                basketId: updatedBasket.basketId,
              }),
            ),
            catchError((error) =>
              of(basketActions.updateBasketShopsFail({ error: error.message })),
            ),
          );
        },
      ),
    );
  });

  public setDropShip$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basketActions.setDropShip),
      concatLatestFrom(() => [
        this.store.pipe(
          select(selectDtoBasketFromEntities),
          filter(isNonNullish),
        ),
        this.store.pipe(
          select(selectShops),
          map(([basketRowGroup]) => basketRowGroup?.details),
          filter<BasketAddressRowsGroupDetails>(Boolean),
        ),
      ]),
      switchMap(([{ payload }, basket, accountDetails]) => {
        const accountsToRemove: BasketAccountAddress = {
          accountId: accountDetails.accountId,
          addressId: accountDetails.addressId,
        };

        const accountsToAdd: BasketAccountAddress = {
          ...accountsToRemove,
          alternativeAddress: payload,
        };

        return this.updateBasketAccountsRequest(
          basket,
          [accountsToAdd],
          [accountsToRemove],
        ).pipe(
          switchMap(() => [
            basketActions.setDropShipSuccess(),
            basketActions.loadBasketById(),
          ]),
          catchError((error) => [
            basketActions.setDropShipFailure({ error: error.message }),
            showSnackBarMessage({
              message: $localize`:CBasket|Failed to set drop ship@@dropShipErrorMessage:Failed to set drop ship`,
              messageType: MessageType.DANGER,
            }),
          ]),
        );
      }),
    );
  });

  public submitForReview$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basketActions.submitForReview),
      concatLatestFrom(() => [
        this.store.pipe(select(selectBasketId), filter<string>(Boolean)),
        this.store.pipe(select(selectSiteId), filter<string>(Boolean)),
        this.store.pipe(select(selectSubSiteId)),
      ]),
      switchMap(
        ([
          {
            payload: { reviewerId, message },
          },
          basketId,
          siteId,
          subSiteId,
        ]) => {
          return this.basketReviewService
            .submitForReview(basketId, reviewerId, {
              message,
              siteId,
              subSiteId,
            })
            .pipe(
              switchMap(() => [
                basketActions.submitForReviewSuccess(),
                basketActions.loadBasketById(),
                basketActions.resetBasket(),
              ]),
              catchError((error) => [
                basketActions.submitForReviewFailure({ error: error.message }),
                showSnackBarMessage({
                  message: $localize`:CBasket|Failed to submit for review@@submitForReviewErrorMessage:Failed to submit for review`,
                  messageType: MessageType.DANGER,
                }),
              ]),
            );
        },
      ),
    );
  });

  public rejectBasket$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(basketActions.rejectBasket),
      tap(() => {
        this.store.dispatch(
          basketActions.setBasketOperableStatus({ isOperable: false }),
        );
      }),
      concatLatestFrom(() => [
        this.store.pipe(select(selectBasketId), filter<string>(Boolean)),
        this.store.pipe(select(selectSiteId), filter<string>(Boolean)),
        this.store.pipe(select(selectSubSiteId)),
      ]),
      switchMap(
        ([
          {
            payload: { message },
          },
          basketId,
          siteId,
          subSiteId,
        ]) => {
          return this.basketReviewService
            .rejectBasket(basketId, {
              message,
              siteId,
              subSiteId,
            })
            .pipe(
              switchMap(() => [
                basketActions.rejectBasketSuccess(),
                basketActions.loadBasketById(),
                basketActions.resetBasket(),
              ]),
              catchError((error) => [
                basketActions.rejectBasketFailure({ error: error.message }),
                showSnackBarMessage({
                  message: $localize`:CBasket|Failed to reject basket@@rejectBasketErrorMessage:Failed to reject basket`,
                  messageType: MessageType.DANGER,
                }),
              ]),
              finalize(() => {
                this.store.dispatch(
                  basketActions.setBasketOperableStatus({ isOperable: true }),
                );
              }),
            );
        },
      ),
    );
  });

  public updateCustomOrderReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.updateCustomOrderReference),
      switchMap(({ customOrderReference }) =>
        this.getBasketHeader().pipe(
          switchMap((basketHeader) => {
            const basketUpdateRequest: BasketUpdateRequest = {
              basketHeader: {
                ...basketHeader,
                clientReferenceNumber: customOrderReference,
              },
              deliveryItems: [],
            };
            return this.updateBasketRequest(basketUpdateRequest).pipe(
              switchMap(() => [
                basketActions.updateCustomOrderReferenceSuccess({
                  basketId: basketHeader.basketId,
                  customOrderReference,
                }),
              ]),
            );
          }),
          catchError((error) => [
            basketActions.updateCustomOrderReferenceFailure({
              error: error.message,
            }),
            showSnackBarMessage({
              message: $localize`:Basket|Update basket client ref number failure message@@BasketUpdateClientRefNumberFailure:Failed to update client reference number.`,
              messageType: MessageType.DANGER,
            }),
          ]),
        ),
      ),
    ),
  );

  public updateAccountDeliveryCustomOrderReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.updateAccountDeliveryClientReferenceNumber),
      switchMap(({ type, ...deliveryAccountToUpdate }) =>
        this.getBasketHeader().pipe(
          switchMap((basketHeader) => {
            const basketUpdateRequest: BasketUpdateRequest = {
              basketHeader,
              deliveryItems: [deliveryAccountToUpdate],
            };

            return this.updateBasketRequest(basketUpdateRequest).pipe(
              map(() =>
                basketActions.updateAccountDeliveryClientReferenceNumberSuccess(
                  deliveryAccountToUpdate,
                ),
              ),
            );
          }),
          catchError((error) => [
            basketActions.updateAccountDeliveryClientReferenceNumberFailure({
              ...deliveryAccountToUpdate,
              error: error.message,
            }),
            showSnackBarMessage({
              message: $localize`:Basket|Update account delivery client ref number failure message@@BasketUpdateAccountDeliveryClientRefNumberFailure:Failed to update client reference number.`,
              messageType: MessageType.DANGER,
            }),
          ]),
        ),
      ),
    ),
  );

  public setCustomOrderReferenceOnAccountDeliveryUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.updateAccountDeliveryClientReferenceNumberSuccess),
      map(({ clientReferenceNumber }) => clientReferenceNumber),
      filter<string>(Boolean),
      concatLatestFrom(() => [
        this.store.pipe(select(selectCustomOrderReferenceNumber)),
        this.store.pipe(select(selectPoNumberRequiredRule)),
      ]),
      // add header po number automatically only if po number is required (needs for Puma)
      filter(([_, headerLevelCustomReference, poNumberRequired]) =>
        Boolean(!headerLevelCustomReference && poNumberRequired),
      ),
      map(([clientReferenceNumber]) =>
        basketActions.updateCustomOrderReference({
          customOrderReference: clientReferenceNumber,
        }),
      ),
    ),
  );

  public updateDeliveryLineCustomOrderReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.updateDeliveryLineClientReferenceNumber),
      tap(() => {
        this.store.dispatch(basketActions.clearBasketValidations());
      }),
      switchMap(({ type, ...deliveryToUpdate }) =>
        this.getBasketHeader().pipe(
          switchMap((basketHeader) => {
            const basketUpdateRequest: BasketUpdateRequest = {
              basketHeader,
              deliveryItems: [],
              deliveries: [deliveryToUpdate],
            };

            return this.updateBasketRequest(basketUpdateRequest).pipe(
              map(() =>
                basketActions.updateDeliveryLineClientReferenceNumberSuccess(
                  deliveryToUpdate,
                ),
              ),
            );
          }),
          catchError((error) => [
            basketActions.updateDeliveryLineClientReferenceNumberFailure({
              ...deliveryToUpdate,
              error: error.message,
            }),
            showSnackBarMessage({
              message: $localize`:Basket|Update delivery line client ref number failure message@@BasketDeliveryLineClientRefNumberFailure:Failed to update client reference number`,
              messageType: MessageType.DANGER,
            }),
          ]),
        ),
      ),
    ),
  );

  public onUpdateFailureMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        basketActions.updateBasketItemsFailure,
        basketActions.updateBasketShopsFail,
      ),
      map(() =>
        showSnackBarMessage({
          message: $localize`:Basket|Update basket fail message@@BasketUpdateFailure:Failed to update basket.`,
          messageType: MessageType.DANGER,
        }),
      ),
    ),
  );

  private readonly preselectBasketOptionEvent$ = this.actions$.pipe(
    ofType(basketActions.loadDtoBasketSuccess),
    switchMap(({ basket }) => {
      // wait until next concatLatestFrom selectors receives correct basket from entity
      // happens after switch basket when load happens before set active basket, and this leads for using previous active basket
      return this.store.select(selectOperableBasketId).pipe(
        filter((basketId) => basket.basketId === basketId),
        take(1),
        takeUntil(timer(5000)),
        map(() => basket),
      );
    }),
    filter((basketDtoResponse) => {
      return (
        isBasketStatusNewish(mapDtoStatus(basketDtoResponse?.status)) &&
        // wait until basket is filled with basket items during create + patch items operation to avoid 412 error with etag
        basketDtoResponse.basketAccounts.length > 0
      );
    }),
    switchMap(() =>
      // wait other basket operation is in progress
      this.store.pipe(select(selectIsOperableStatus), filter(Boolean), take(1)),
    ),
  );

  public preselectPropertyBag$ = createEffect(() =>
    this.preselectBasketOptionEvent$.pipe(
      withLatestFrom(this.store.select(selectPreselectPropertyBagRule)),
      filter(([_, preselectEnabled]) => !!preselectEnabled),
      // wait active account changed in case of switch basket, add/remove store to get correct account properties
      switchMap(() => {
        return combineLatest([
          this.store.pipe(
            select(selectOperableDtoBasketFromEntity),
            filter(isNonNullish),
          ),
          this.store.pipe(
            select(selectActiveAccount),
            filter(isNonNullish),
            // wait until account details are loaded
            filter((account) => account.hasOwnProperty('properties')),
          ),
        ]).pipe(
          filter(([basket, account]) => {
            return basket.basketAccounts.some(
              (ba) => ba.account.accountId === account.accountId,
            );
          }),
          take(1),
        );
      }),
      concatLatestFrom(() => [
        this.store.pipe(select(selectPropertyBagForAutoFill)),
      ]),
      filter(([, propertyBag]) => Object.keys(propertyBag).length > 0),
      map(([_, propertyBag]) =>
        basketActions.updatePropertyBag({
          propertyBag,
        }),
      ),
    ),
  );

  public preselectSingleShippingOption$ = createEffect(() =>
    this.preselectBasketOptionEvent$.pipe(
      concatLatestFrom(() => [
        this.store.pipe(select(selectAvailableShippingOptions)),
        this.store.pipe(select(selectSelectedShippingOptionId)),
      ]),
      filter(
        ([, shippingOptions, selectedShippingOptionId]) =>
          !selectedShippingOptionId &&
          shippingOptions?.filter(Boolean).length === 1,
      ),
      concatLatestFrom(() => [
        this.store.pipe(select(selectAvailableShippingOptions)),
      ]),
      switchMap(([_, shippingOptions]) => [
        basketActions.updateSelectedShippingOption({
          selectedShippingOptionId: shippingOptions[0].id,
        }),
        showSnackBarMessage({
          message: $localize`:Basket|Shipping option preselected@@shippingOptionPreselected:Shipping option is preselected`,
          messageType: MessageType.INFO,
        }),
      ]),
    ),
  );

  public preselectShippingOptionByRule$ = createEffect(() =>
    this.preselectBasketOptionEvent$.pipe(
      concatLatestFrom(() => [
        this.store.pipe(select(selectAvailableShippingOptions)),
        this.store.pipe(select(selectSelectedShippingOptionId)),
        this.store.pipe(select(selectShippingOptionUsePreferredRule)),
        this.store.pipe(select(selectPreferredShippingOptionId)),
      ]),
      filter(
        ([
          ,
          shippingOptions,
          selectedShippingOptionId,
          usePreferred,
          preferredShippingOptionId,
        ]) =>
          !!usePreferred &&
          !!preferredShippingOptionId &&
          !selectedShippingOptionId &&
          // single option will be preselected by preselectSingleShippingOption$
          shippingOptions?.filter(Boolean).length > 1 &&
          shippingOptions.some(({ id }) => id === preferredShippingOptionId),
      ),
      concatLatestFrom(() => [
        this.store.pipe(select(selectPreferredShippingOptionId)),
      ]),
      map(([_, preferredShippingOptionId]) =>
        basketActions.updateSelectedShippingOption({
          selectedShippingOptionId: preferredShippingOptionId as string,
        }),
      ),
    ),
  );

  public updateSelectShippingOption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.updateSelectedShippingOption),
      switchMap(({ selectedShippingOptionId }) => {
        return this.getBasketHeader().pipe(
          map((basketHeader) => {
            return {
              ...basketHeader,
              selectedShippingOptionId,
            };
          }),
        );
      }),
      switchMap((basketHeader) => {
        return this.updateBasketRequest({
          basketHeader,
          deliveryItems: [],
        }).pipe(
          map(() =>
            basketActions.updateSelectedShippingOptionSuccess({
              selectedShippingOptionId: basketHeader.selectedShippingOptionId,
            }),
          ),
          catchError((error) =>
            of(
              basketActions.updateSelectedShippingOptionFailed({
                error: error.message,
              }),
            ),
          ),
        );
      }),
    ),
  );

  public updatePreferredShippingOption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.updatePreferredShippingOption),
      concatLatestFrom(() => [
        this.store.pipe(select(selectShippingOptionUsePreferredRule)),
        this.store.pipe(select(selectPreferredShippingOptionId)),
      ]),
      filter(
        ([
          { preferredShippingOptionId },
          usePreferredOption,
          currentPreferredOptionId,
        ]) =>
          Boolean(
            usePreferredOption &&
              preferredShippingOptionId !== currentPreferredOptionId,
          ),
      ),
      map(([{ preferredShippingOptionId }]) =>
        postUserPreferences({
          preferences: {
            preferredShippingOption: preferredShippingOptionId,
          },
        }),
      ),
    ),
  );

  public updatePropertyBag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.updatePropertyBag),
      switchMap(({ propertyBag }) => {
        return this.getBasketHeader().pipe(
          map((basketHeader) => {
            const updatedPropertyBag = Object.fromEntries(
              Object.entries({
                ...basketHeader.propertyBag,
                ...propertyBag,
                // keep empty string value to figure out it was reset by User action
              }).map(([key, value]) => [key, value ?? '']),
            );

            return {
              ...basketHeader,
              propertyBag: updatedPropertyBag,
            };
          }),
          switchMap((basketHeader) => {
            return this.updateBasketRequest({
              basketHeader,
              deliveryItems: [],
            }).pipe(
              map(() => basketActions.updatePropertyBagSuccess()),
              catchError(() => [
                basketActions.updatePropertyBagFailure({
                  propertyBag,
                }),
                showSnackBarMessage({
                  message: $localize`:cBasket|Update property bag failure message@@BasketUpdatePropertyBagFailure:Failed to update basket property`,
                  messageType: MessageType.DANGER,
                }),
              ]),
            );
          }),
        );
      }),
    ),
  );

  public updateShipInFullOption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.updateShipInFullOption),
      switchMap(({ userSpecifiedGroupedDelivery }) => {
        return this.getBasketHeader().pipe(
          map((basketHeader) => {
            return {
              ...basketHeader,
              userSpecifiedGroupedDelivery,
            };
          }),
        );
      }),
      switchMap((basketHeader) => {
        return this.updateBasketRequest({
          basketHeader,
          deliveryItems: [],
        }).pipe(
          tap((update) => {
            if (
              Boolean(update.userSpecifiedGroupedDelivery) !==
              basketHeader.userSpecifiedGroupedDelivery
            ) {
              throw new Error('Failed to update ship in full option');
            }
          }),
          map(() =>
            basketActions.updateShipInFullOptionSuccess({
              basketId: basketHeader.basketId,
              userSpecifiedGroupedDelivery:
                basketHeader.userSpecifiedGroupedDelivery,
            }),
          ),
          catchError((error) => [
            basketActions.updateShipInFullOptionFailed({
              basketId: basketHeader.basketId,
              error: error.message,
            }),
            showSnackBarMessage({
              message: $localize`:cBasket|Update ship in full basket fail message@@BasketUpdateShipInFullFailure:Failed to update ship in full option`,
              messageType: MessageType.DANGER,
            }),
          ]),
        );
      }),
    ),
  );

  public setBasketIdForGtmPageTrack$ = createEffect(() =>
    this.store.pipe(
      select(selectActiveBasketId),
      map((id: string) => {
        return gtmTrackActions.gtmSetUserProperty({
          value: {
            basket_id: id,
          },
        });
      }),
    ),
  );

  private updateBasketAccountsRequest(
    basket: ExtendedDtoBasket,
    accountsToAdd: BasketAccountAddress[],
    accountsToRemove: BasketAccountAddress[],
  ) {
    return this.basketService
      .updateAccounts(basket, accountsToAdd, accountsToRemove)
      .pipe(
        tap((basketResponse) => {
          this.store.dispatch(
            basketActions.loadDtoBasketSuccess({ basket: basketResponse }),
          );

          this.store.dispatch(basketActions.clearBasketValidations());
        }),
      );
  }

  private updateBasketRequest(basketUpdateRequest: BasketUpdateRequest) {
    this.store.dispatch(
      setBasketOperableStatus({
        isOperable: false,
      }),
    );
    const basketId = basketUpdateRequest.basketHeader.basketId;
    return this.getDtoBasketToUpdate(basketId).pipe(
      map((basket) => mapBasketUpdateRequest(basketUpdateRequest, basket)),
      switchMap((patchDtoBasket) => {
        return this.basketService.updateDtoBasket(patchDtoBasket).pipe(
          tap((updatedBasket) => {
            this.store.dispatch(
              basketActions.writeItemsInsertOrUpdate({
                items: patchDtoBasket.itemsToInsertOrUpdate,
              }),
            );
            this.store.dispatch(basketActions.updateDtoBasketSuccess());
            this.store.dispatch(
              basketActions.loadDtoBasketSuccess({ basket: updatedBasket }),
            );
            this.store.dispatch(basketActions.clearBasketValidations());
          }),
          catchError((e) => {
            if (e.status === 412) {
              this.store.dispatch(pollBasket());
            }
            return throwError(() => e);
          }),
        );
      }),
      finalize(() => {
        this.store.dispatch(
          setBasketOperableStatus({
            isOperable: true,
          }),
        );
      }),
    );
  }

  private getDtoBasketToUpdate(
    basketId: string,
  ): Observable<basketDto.ExtendedDtoBasket> {
    return this.store.pipe(
      select(selectBasketDtoFromEntitiesById(basketId)),
      take(1),
      switchMap((basket) => {
        if (basket) {
          return of(basket);
        }
        // temporary backward compatibility when grouped basket comes from gql
        return this.basketService.getDtoBasket(basketId, true);
      }),
    );
  }

  /**
   * If there's no active basket a new one is created and the user preferences updated.
   * Otherwise it returns BasketHeader with all existing data necessary for patch
   */
  private getBasketHeader(): Observable<BasketHeader> {
    return this.store.pipe(
      select(selectOperableBasketId),
      take(1),
      switchMap((basketId) => {
        if (basketId) {
          return of({ basketId } as BasketHeader).pipe(
            concatLatestFrom(() => [
              this.store.pipe(select(selectCustomOrderReferenceNumber)),
              this.store.pipe(select(selectSelectedShippingOptionId)),
              this.store.pipe(select(selectShipInFullOption)),
              this.store.pipe(select(selectBasketPropertyBag)),
            ]),
            // we have to prepare basketHeader with existing data because backend will remove not sent fields
            map(
              ([
                basketHeader,
                clientReferenceNumber,
                selectedShippingOptionId,
                userSpecifiedGroupedDelivery,
                propertyBag,
              ]) => ({
                ...basketHeader,
                clientReferenceNumber,
                selectedShippingOptionId,
                propertyBag: propertyBag ?? {},
                userSpecifiedGroupedDelivery:
                  userSpecifiedGroupedDelivery || undefined,
              }),
            ),
          );
        }

        return this.basketService.createBasket().pipe(
          tap((basket) => {
            this.store.dispatch(loadDtoBasketSuccess({ basket }));
            this.store.dispatch(
              basketActions.setActiveBasketId({ basketId: basket.basketId }),
            );
            this.store.dispatch(
              gtmTrackActions.gtmTrackEvent({
                action: BasketAction.CREATE,
                properties: {
                  category: BASKET_CATEGORY,
                  label: basket.basketId,
                },
              }),
            );
          }),
          concatLatestFrom(() =>
            this.store.pipe(select(selectActiveAccountId)),
          ),
          filter(([, accountId]) => !!accountId),
          switchMap(([basket, accountId]) =>
            this.userService
              .setActiveBasketId(accountId as string, basket.basketId)
              .pipe(map(() => ({ basketId: basket.basketId }))),
          ),
        );
      }),
    );
  }
}

function getBasketAccountsDiff(
  accounts1: BasketAccountAddress[],
  accounts2: BasketAccountAddress[],
): BasketAccountAddress[] {
  return accounts1.filter((acc1) =>
    accounts2.every((acc2) => {
      return (
        `${acc1.accountId}${acc1.addressId}` !==
        `${acc2.accountId}${acc2.addressId}`
      );
    }),
  );
}
